import React, { createContext, useContext, useState, ReactNode } from "react";

export interface Group {
  id: string;
  name: string;
}

interface GroupContextType {
  groups: Group[];
  addGroup: (name: string) => boolean;
  removeGroup: (id: string) => void;
}

const GroupContext = createContext<GroupContextType | undefined>(undefined);

export const useGroups = (): GroupContextType => {
  const context = useContext(GroupContext);
  if (!context) throw new Error("useGroups must be used within GroupProvider");
  return context;
};

export const GroupProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [groups, setGroups] = useState<Group[]>([]);

  const addGroup = (name: string): boolean => {
    const trimmed = name.trim();
    const exists = groups.some(
      (g) => g.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) return false;

    const newGroup: Group = {
      id: `group-${Date.now()}`,
      name: trimmed,
    };

    setGroups((prev) => [...prev, newGroup]);
    return true;
  };

  const removeGroup = (id: string) => {
    setGroups((prev) => prev.filter((group) => group.id !== id));
  };

  return (
    <GroupContext.Provider value={{ groups, addGroup, removeGroup }}>
      {children}
    </GroupContext.Provider>
  );
};
